import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Anchor, Box, Group, Paper, Stack, Text } from "@mantine/core";
import { CampaignProductResults } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { ReportSection } from "../components/ReportSection";
import { barLinkForm, deriveTestujtoLinks, EshopBar, ProductLink } from "../derive";

// Distinct, intentional colours for the e-shop destination bars (cycled). Chosen to sit
// apart from the orange/blue/green of the funnel's first three steps.
const ESHOP_GRADIENTS = [
    "linear-gradient(90deg, #7048E8, #9775FA)", // violet
    "linear-gradient(90deg, #0C8599, #22B8CF)", // cyan
    "linear-gradient(90deg, #9C36B5, #CC5DE8)", // grape
    "linear-gradient(90deg, #C2255C, #F06595)", // rose
    "linear-gradient(90deg, #1864AB, #4DABF7)", // deep blue
];

// A product page opens in a new tab without handing it the report (A6).
const ProductPageLink = ({ href, children }: { href: string; children: ReactNode }) => (
    <Anchor href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>
        {children}
    </Anchor>
);

const LinkMissing = () => {
    const { t } = useTranslation(["common"]);
    return (
        <Text span size="xs" weight={600} color="red">
            {t("campaign_report_link_missing")}
        </Text>
    );
};

interface BarLabelProps {
    // The label text; with `name`, the text before the portal name ("Recenze na" + " Heureka").
    label: string;
    name?: string;
    links: ProductLink[];
    // No link is expected for this bar, so none is reported missing (the Testuj.to row of a
    // campaign no product of which carries `tt`, A9b).
    noLinkExpected?: boolean;
}

// The bar's label with its product links (R8, A8): one URL shared by every product → the portal
// name (or the whole Testuj.to label) is the link; differing or partial URLs → a "<product> ↗" link
// per product, a product without one followed by "Nebyl dodán link"; no URL at all → the plain
// label followed by "Nebyl dodán link".
const BarLabel = ({ label, name, links, noLinkExpected }: BarLabelProps) => {
    const form = barLinkForm(links);
    const linked = (text: string) =>
        form.kind === "single" ? <ProductPageLink href={form.url}>{text}</ProductPageLink> : text;
    return (
        <Box style={{ width: 240, flex: "none" }}>
            <Text size="sm" weight={600} color="#0F1B3D">
                {name ? (
                    <>
                        {label} {linked(name)}
                    </>
                ) : (
                    linked(label)
                )}
            </Text>
            {form.kind === "perProduct" && (
                <Stack spacing={2} mt={4}>
                    {form.links.map((link) => (
                        <Text key={link.productId} size="xs" color="#0F1B3D">
                            {link.url ? (
                                <ProductPageLink href={link.url}>{link.productName} ↗</ProductPageLink>
                            ) : (
                                <>
                                    {link.productName} <LinkMissing />
                                </>
                            )}
                        </Text>
                    ))}
                </Stack>
            )}
            {form.kind === "none" && !noLinkExpected && (
                <Text size="xs">
                    <LinkMissing />
                </Text>
            )}
        </Box>
    );
};

interface FunnelBarProps extends BarLabelProps {
    value: number;
    gradient: string;
    // Bar width as a % of its group's scale (see ReportFunnel: the acquisition steps scale to
    // total applications, the e-shop bars scale to the review pool so they read fully).
    widthPct: number;
}

const FunnelBar = ({ label, name, links, noLinkExpected, value, gradient, widthPct }: FunnelBarProps) => (
    <Group spacing={16} noWrap align="center">
        {/* Label sits outside the bar so a 0 value reads as an empty track, not a
            misleading min-width coloured bar. */}
        <BarLabel label={label} name={name} links={links} noLinkExpected={noLinkExpected} />
        <Box style={{ flex: 1, height: 28, background: "#EEF1F6", borderRadius: 8 }}>
            <Box style={{ width: `${widthPct}%`, background: gradient, borderRadius: 8, height: 28 }} />
        </Box>
        <Text
            weight={700}
            color="#0F1B3D"
            style={{ minWidth: 64, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
        >
            {value.toLocaleString("cs-CZ")}
        </Text>
    </Group>
);

export interface ReportFunnelProps {
    campaign: CampaignQueryDataType;
    // Product results (stored or recomputed) — the "Reviews on Testuj.to" step sums approvedCount.
    results?: CampaignProductResults[];
    // Per-e-shop-destination review counts (live; isShipped for integrated, checkbox for custom).
    eshopBars?: EshopBar[];
}

// Review distribution across destinations: reviews on Testuj.to plus one bar per e-shop, all
// scaled to the review pool so they read fully. (Applications / selected / target are shown as
// cards in the "what we tested" section, not here.) Testuj.to keeps its green so it reads as the
// platform rather than an e-shop.
export const ReportFunnel = ({ campaign, results, eshopBars }: ReportFunnelProps) => {
    const { t } = useTranslation(["common"]);

    const locale = campaign?.mainTenant?.locale || campaign?.tenant?.locale;

    const totalReviews = (results || []).reduce((sum, product) => sum + (product?.approvedCount || 0), 0);
    const eshopSteps = eshopBars || [];
    // Each product's reviewed page on the main tenant's domain (A4).
    const testujtoLinks = deriveTestujtoLinks(
        campaign?.products,
        campaign?.mainTenant?.domainName,
        campaign?.productReviewDestinations
    );

    const distributionSteps: Array<BarLabelProps & { key: string; value: number; gradient: string }> = [
        {
            key: "__tt__",
            label: t("campaign_report_reviews_on_tt"),
            links: testujtoLinks,
            // No product carries `tt`: the channel was never used, so no link is missing (A9b).
            noLinkExpected: testujtoLinks.length === 0,
            value: totalReviews,
            gradient: "linear-gradient(90deg, #168F5B, #33B277)",
        },
        ...eshopSteps.map((destination, index) => ({
            key: destination.key,
            // The name stays separate text, not interpolated into the key: it may become a link.
            label: t("campaign_report_reviews_on"),
            name: destination.name,
            links: destination.links,
            value: destination.count,
            gradient: ESHOP_GRADIENTS[index % ESHOP_GRADIENTS.length],
        })),
    ];

    // Scale to the review pool (never below the largest bar) so counts read fully.
    const scaleMax = Math.max(...distributionSteps.map((step) => step.value), 1);

    return (
        <ReportSection
            title={t("campaign_report_funnel")}
            meta={
                locale && (
                    <>
                        {t("campaign_report_locale")}{" "}
                        <strong style={{ color: "#0F1B3D" }}>{locale.toUpperCase()}</strong>
                    </>
                )
            }
        >
            <Paper withBorder radius={16} p={24}>
                <Text
                    size="xs"
                    weight={700}
                    color="#7A8699"
                    mb={12}
                    style={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                    {t("campaign_report_eshop_distribution")}
                </Text>
                <Stack spacing={14}>
                    {distributionSteps.map((step) => (
                        <FunnelBar
                            key={step.key}
                            label={step.label}
                            name={step.name}
                            links={step.links}
                            noLinkExpected={step.noLinkExpected}
                            value={step.value}
                            gradient={step.gradient}
                            widthPct={Math.min(100, (step.value / scaleMax) * 100)}
                        />
                    ))}
                </Stack>
            </Paper>
        </ReportSection>
    );
};
